import { lazy, Suspense, useState, type FC } from 'react'
import { useSideNavExpandedLayout } from '@/contexts/side-nav-expanded-context'
import type { RouteObject } from 'react-router'
import { Outlet } from 'react-router-dom'
import { IssuerGuard } from '@/guards/issuer-guard'
import { AuthGuard } from '@/guards/auth-guard'
import { Issuer } from '@/types/auth'
import { Layout as DashboardLayout } from '@/layouts/dashboard'
import { wrapWithErrorBoundary } from '@/routes/wrapWithErrorBoundary'
import type { DashboardOutletContextValue } from '@/layouts/dashboard/dashboard-outlet-context'
import { Role } from '@/api'
import { accountNestedRoute } from './account'
import { documentsNestedRoute } from './documents'
import { settingsNestedRoute } from './settings'
import { customerFlowNestedRoute } from './customerflow'

const DashboardV2Page = lazy(() => import('@/pages/dashboard/dashboard-v2'))
const ManagementConsolePage = lazy(() => import('@/pages/managementConsole'))
const MCEstablishmentDetail = lazy(
  () => import('@/pages/managementConsole/establishmentDetail')
)

/** Le document source seul dans un onglet (second écran) : sans la navigation du dashboard. */
const DeclarationSourcePage = lazy(
  () => import('@/pages/documents/declaration/source')
)

const WrappedDashboardV2Page = wrapWithErrorBoundary(DashboardV2Page)
const WrappedDeclarationSourcePage = wrapWithErrorBoundary(
  DeclarationSourcePage
)
/** Tarifill seul dans un onglet (second écran), relié à la déclaration (TEC-1807). */
const DeclarationTarifillPage = lazy(
  () => import('@/pages/documents/declaration/tarifill')
)
const WrappedDeclarationTarifillPage = wrapWithErrorBoundary(
  DeclarationTarifillPage
)
const WrapperManagementConsolePage = wrapWithErrorBoundary(
  ManagementConsolePage
)
const WrappedEstablishmentDetailPage = wrapWithErrorBoundary(
  MCEstablishmentDetail
)

const ManagementConsoleRoutesOutlet: FC = () => (
  <AuthGuard role={[Role.Superadmin, Role.Admin, Role.Manager]}>
    <Suspense>
      <Outlet />
    </Suspense>
  </AuthGuard>
)

export const DashboardLayoutWrapper = (props: any) => {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const { sideNavExpanded, setSideNavExpanded } = useSideNavExpandedLayout()

  const outletContext: DashboardOutletContextValue = {
    setIsFullscreen,
    sideNavExpanded,
    setSideNavExpanded,
  }

  return (
    <IssuerGuard issuer={Issuer.JWT}>
      <DashboardLayout
        isFullscreen={isFullscreen}
        sideNavExpanded={sideNavExpanded}
        setSideNavExpanded={setSideNavExpanded}
      >
        <AuthGuard role={null}>
          <Suspense>
            <Outlet context={outletContext} />
          </Suspense>
        </AuthGuard>
      </DashboardLayout>
    </IssuerGuard>
  )
}

export const dashboardRoutes: RouteObject[] = [
  {
    path: '/',
    element: <DashboardLayoutWrapper />,
    children: [
      {
        index: true,
        element: <WrappedDashboardV2Page />,
        handle: {
          documentTitle: { namespace: 'nav', key: 'dashboard' },
        },
      },
      {
        path: 'management-console',
        element: <ManagementConsoleRoutesOutlet />,
        children: [
          {
            index: true,
            element: <WrapperManagementConsolePage />,
            handle: {
              documentTitle: { namespace: 'nav', key: 'managementConsole' },
            },
          },
          {
            path: 'establishment/:id',
            element: <WrappedEstablishmentDetailPage />,
            handle: {
              documentTitle: { namespace: 'nav', key: 'managementConsole' },
            },
          },
        ],
      },
      documentsNestedRoute,
      settingsNestedRoute,
      accountNestedRoute,
      customerFlowNestedRoute,
    ],
  },
  {
    path: '/documents/:id/declaration/source',
    handle: {
      documentTitle: {
        namespace: 'extraction',
        key: 'stepDocumentWindowTitle',
      },
    },
    element: (
      <IssuerGuard issuer={Issuer.JWT}>
        <AuthGuard role={null}>
          <Suspense>
            <WrappedDeclarationSourcePage />
          </Suspense>
        </AuthGuard>
      </IssuerGuard>
    ),
  },
  {
    path: '/documents/:id/declaration/tarifill',
    handle: {
      documentTitle: {
        namespace: 'tariffClassification',
        key: 'productName',
      },
    },
    element: (
      <IssuerGuard issuer={Issuer.JWT}>
        <AuthGuard role={null}>
          <Suspense>
            <WrappedDeclarationTarifillPage />
          </Suspense>
        </AuthGuard>
      </IssuerGuard>
    ),
  },
]
